import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDefaultSetItemRestSec } from '../../../../hooks/use-default-set-item-rest-sec';
import { useProgramItemExercises } from '../../../../hooks/useProgramItemExercises';
import { useTrainingExercises } from '../../../../hooks/useTrainingExercises';
import { usePostPutProgramSet } from '../../../../redux/api/programsAPI';
import { useGetMyDetails } from '../../../../redux/api/users/usersApi';
import { IExercise } from '../../../../types/interfaces/exercise';
import { IProgramSet } from '../../../../types/interfaces/program-set';
import { IProgramSetItemOption } from '../../../../types/interfaces/program-set-item-option';
import { ITrainingSet } from '../../../../types/interfaces/training-set';
import { ITrainingSetItem } from '../../../../types/interfaces/training-set-item';
import { ITrainingSetItemOption } from '../../../../types/interfaces/training-set-item-option';
import {
  findExerciseResultByExerciseId,
  mergeProgramResultIntoOptions,
  mergeTrainingResultIntoOptions,
} from '../../../../utils/training/exercise-result-autofill';
import {
  copyProgramSetItemToNext,
  copyTrainingSetItemToNext,
  getEmptyProgramSetFromProgramSet,
  getEmptySetItem,
  getProgramSetFromTrainingSet,
  getSetFromSavedProgramSet,
  orderExerciseOptions,
} from '../../../../utils/training/set-utils';
import { successToastMessage } from '../../../layout/toast-root/toast';
import {
  IUseSetGroupEditTable,
  SetGroupEditTableMode,
  SetGroupFieldName,
} from '../interfaces';

export function useSetEditComponent(
  set: IProgramSet | ITrainingSet,
  useSetGroupEditTable: IUseSetGroupEditTable<IProgramSet | ITrainingSet>,
) {
  const { mode, onChangeSet, onRemoveSetsBySetNumber } = useSetGroupEditTable;
  const [showSavedProgramItemModal, setShowSavedProgramItemModal] =
    useState(false);
  const [showNewExerciseModal, setShowNewExerciseModal] = useState(false);
  const [showSaveSetModal, setShowSaveSetModal] = useState(false);
  const { exercises } =
    mode === SetGroupEditTableMode.program
      ? useProgramItemExercises({
          programItemId: (set as IProgramSet)?.programItemId,
        })
      : useTrainingExercises((set as ITrainingSet)?.trainingId);
  const [postPutProgramSet] = usePostPutProgramSet();
  const { data: user } = useGetMyDetails();
  const { t } = useTranslation();
  const exerciseResults = useSetGroupEditTable.exerciseResults ?? undefined;
  const { resolveRestForFirstItemAfterExerciseSelect } =
    useDefaultSetItemRestSec();

  function onAddSetItemClick() {
    const setItemsFieldName = SetGroupFieldName[mode].setItemsFieldName;

    const maxOrderNumber = Math.max(
      ...set[setItemsFieldName].map((item) => item.orderNumber),
    );
    const maxSetItem = set[setItemsFieldName].find(
      (orderNumber) => orderNumber.orderNumber === maxOrderNumber,
    );

    const updatedProgramSet = {
      ...set,
      [setItemsFieldName]: [
        ...set[setItemsFieldName],
        (() => {
          if (mode === SetGroupEditTableMode.program) {
            return copyProgramSetItemToNext(maxSetItem);
          }

          const nextItem = copyTrainingSetItemToNext(maxSetItem);
          if (useSetGroupEditTable.options.copyLastTrainingSetItemStatusOnAdd) {
            nextItem.status = (maxSetItem as ITrainingSetItem).status;
          }
          return nextItem;
        })(),
      ],
    };

    onChangeSet(updatedProgramSet);
  }

  function onChangeExercise(exercise?: IExercise) {
    const setItemsFieldName = SetGroupFieldName[mode].setItemsFieldName;

    if (!exercise) {
      onChangeSet({
        ...set,
        exercise: null,
        exerciseId: null,
        [setItemsFieldName]: [],
      });
      return;
    }

    const orderedExercise = orderExerciseOptions(exercise);

    const result = findExerciseResultByExerciseId(
      exerciseResults,
      orderedExercise?.id,
    );

    const exerciseOptions =
      exercises?.find((e) => String(e.id) === String(orderedExercise?.id))
        ?.options ?? [];

    const emptySetItem = getEmptySetItem(
      mode,
      0,
      exerciseOptions,
      resolveRestForFirstItemAfterExerciseSelect(result),
    );

    if (result) {
      const fieldName = SetGroupFieldName[mode].optionsFieldName;
      if (mode === SetGroupEditTableMode.program) {
        emptySetItem[fieldName] = mergeProgramResultIntoOptions(
          emptySetItem[fieldName] as IProgramSetItemOption[],
          result,
        );
      } else {
        emptySetItem[fieldName] = mergeTrainingResultIntoOptions(
          emptySetItem[fieldName] as ITrainingSetItemOption[],
          result,
        );
      }
    }

    const updatedSet: IProgramSet | ITrainingSet = {
      ...set,
      exercise: orderedExercise || null,
      exerciseId: orderedExercise?.id || null,
      [setItemsFieldName]: [emptySetItem],
    };

    onChangeSet(updatedSet);
  }

  function onReplaceSavedProgramSet(savedSet: IProgramSet) {
    const newSet = getSetFromSavedProgramSet(mode, savedSet, {
      setNumber: set.setNumber,
      orderNumber: set.orderNumber,
      trainingId: (set as ITrainingSet).trainingId,
      programItemId: (set as IProgramSet).programItemId,
      ownerId: (set as IProgramSet).ownerId,
    });

    if (mode === SetGroupEditTableMode.program) {
      if ((set as IProgramSet).title) {
        (newSet as IProgramSet).title = (set as IProgramSet).title;
      }

      if ((set as IProgramSet).description) {
        (newSet as IProgramSet).description = (set as IProgramSet).description;
      }
    }

    // onRemoveSetsBySetNumber && onRemoveSetsBySetNumber(set.setNumber, set.orderNumber);
    onChangeSet(newSet);
  }

  function onSaveSet(title: string) {
    let newProgramSet: IProgramSet = null;

    if (mode === SetGroupEditTableMode.program) {
      newProgramSet = getEmptyProgramSetFromProgramSet(set as IProgramSet);
    } else {
      newProgramSet = getProgramSetFromTrainingSet(set as ITrainingSet);
    }

    newProgramSet.title = title;
    newProgramSet.ownerId = user?.id;
    newProgramSet.setNumber = 0;
    newProgramSet.orderNumber = 0;
    newProgramSet.exercise = null;

    return postPutProgramSet({
      setId: undefined,
      body: newProgramSet,
    })
      .unwrap()
      .then((response) => {
        setShowSaveSetModal(false);
        successToastMessage(t('setTable.setSaved'));
        return response;
      })
      .catch((e) => {
        console.log(e);
        return null;
      });
  }

  return {
    exercises,
    onAddSetItemClick,
    onChangeExercise,
    showSavedProgramItemModal,
    setShowSavedProgramItemModal,
    onReplaceSavedProgramSet,
    showNewExerciseModal,
    setShowNewExerciseModal,
    showSaveSetModal,
    setShowSaveSetModal,
    onSaveSet,
  };
}
